import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  GlobalStyles,
  Typography,
} from '@mui/material';
import { Close, Print } from '@mui/icons-material';

import { AudienceProvider } from '../audience';
import { CircuitPrintFlag, CircuitPrintModel } from '../circuitPrint';
import { CircuitItineraryChain } from './CircuitItineraryChain';

interface Props {
  model?: CircuitPrintModel;
  onClose: () => void;
}

/**
 * El único nodo que sobrevive a la impresión. Se nombra una sola vez para que
 * el CSS de `@media print` y el JSX no puedan desalinearse.
 */
const PRINT_ROOT = 'circuit-print-root';

/**
 * El papel. Tres cosas que se ven recién al imprimir y que este bloque resuelve:
 *
 * 1. **La app entera sale del documento** (`#root` en `display: none`): no
 *    alcanza con ocultarla, porque su alto seguiría generando páginas en
 *    blanco. El diálogo vive en un portal fuera de `#root`, así que sobrevive.
 * 2. **El diálogo deja de ser un diálogo**: sin `position`, sin `overflow` y
 *    sin alto máximo, si no el papel sale recortado a lo que se veía en
 *    pantalla y en una columna angosta (el ancho del `Paper`).
 * 3. **Cada día es un bloque indivisible** y el pie se repite por página.
 */
const printStyles = {
  '@media print': {
    '@page': { size: 'A4', margin: '14mm' },
    // Fuera del papel TODO lo que no contiene el documento: la app, los otros
    // portales (drawers, la propia ficha) y sus altos, que si no generan
    // páginas en blanco antes del documento. `:has()` es de Chrome 105+, que
    // es donde se imprime; si el motor no lo soporta, la regla entera se
    // descarta y queda la de abajo, que oculta sin recuperar el espacio.
    'body > *:not(:has(#circuit-print-root))': { display: 'none !important' },
    'body *': { visibility: 'hidden' },
    [`#${PRINT_ROOT}, #${PRINT_ROOT} *`]: {
      visibility: 'visible',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    },
    'html, body': { height: 'auto !important', overflow: 'visible !important' },
    '.MuiDialog-root, .MuiDialog-container, .MuiPaper-root, .MuiDialogContent-root':
      {
        position: 'static !important',
        overflow: 'visible !important',
        height: 'auto !important',
        maxHeight: 'none !important',
        boxShadow: 'none !important',
        margin: '0 !important',
        padding: '0 !important',
        maxWidth: 'none !important',
        width: 'auto !important',
        background: '#fff',
      },
    [`#${PRINT_ROOT}`]: {
      maxWidth: 'none !important',
      margin: '0 !important',
      padding: '0 !important',
      boxShadow: 'none !important',
      background: '#fff',
      color: '#221f26',
    },
    // El pie fijo se repite en TODAS las páginas impresas (Chrome), que es el
    // criterio: un PDF reenviado no puede tener una página sin fecha.
    [`#${PRINT_ROOT} .print-foot`]: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
    },
    // Un día partido entre dos páginas es un relato que se lee dos veces.
    [`#${PRINT_ROOT} .print-block`]: { breakInside: 'avoid' },
    '.MuiDialogActions-root, .MuiBackdrop-root': { display: 'none !important' },
  },
} as const;

function FlagChip({ flag }: { flag: CircuitPrintFlag }) {
  const { t } = useTranslation();
  const label = {
    guaranteed: t('travelServices.circuits.guaranteedDeparture'),
    onRequest: t('travelServices.circuits.toConfirmByOperator'),
    passport: t('travelServices.circuits.passportFlag'),
  }[flag];
  const tone = {
    guaranteed: { bg: '#e6f2e7', ink: '#1e5c22' },
    onRequest: { bg: '#fdeede', ink: '#8a4200' },
    passport: { bg: '#e7eff9', ink: '#0b4a85' },
  }[flag];

  return (
    <Box
      component="span"
      sx={{
        fontSize: 9.5,
        fontWeight: 600,
        borderRadius: 0.75,
        px: 0.875,
        py: 0.25,
        bgcolor: tone.bg,
        color: tone.ink,
      }}
    >
      {label}
    </Box>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="p"
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#6b6675',
        mt: 2,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * El circuito impreso, para el cliente. Es la pieza que el asesor manda por
 * WhatsApp y que vive semanas en ese chat, así que:
 *
 * - **Es RETAIL por construcción**: el árbol va envuelto en
 *   `AudienceProvider audience="retail"` y todo lo que imprime sale de
 *   `CircuitPrintModel`, donde el neto no existe (ver `circuitPrintModel`).
 * - **La marca es de la AGENCIA**, no del operador ni nuestra: el nombre y el
 *   contacto del asesor salen de la agencia seleccionada. El operador aparece
 *   como texto ("Operado por X").
 * - **Declara su edad**: la fecha de generación va en la carátula, pegada al
 *   precio indicativo, y otra vez en el pie de cada página.
 *
 * Sin dependencias nuevas: es una vista con `@media print` y `window.print()`;
 * el PDF lo genera el navegador.
 */
export function CircuitPrintDialog({ model, onClose }: Props) {
  const { t } = useTranslation();

  return (
    <Dialog open={Boolean(model)} onClose={onClose} maxWidth="md" fullWidth>
      {/* Las reglas de impresión existen SOLO mientras el documento está en
          pantalla: son globales y agresivas (sacan del papel todo lo que no es
          el documento), así que un Ctrl+P de la pantalla normal no puede
          encontrárselas puestas. */}
      {model && <GlobalStyles styles={printStyles} />}
      <DialogContent dividers sx={{ bgcolor: 'grey.100' }}>
        {model && (
          <AudienceProvider audience="retail">
            <Box
              id={PRINT_ROOT}
              sx={{
                bgcolor: '#fff',
                color: '#221f26',
                mx: 'auto',
                maxWidth: 780,
                p: 3,
                pb: 6,
                boxShadow: { xs: 0, sm: 1 },
              }}
            >
              {/* Encabezado: la marca de la agencia y quién cotiza. */}
              <Box
                className="print-block"
                display="flex"
                alignItems="flex-start"
                gap={2}
                pb={1.25}
                sx={{ borderBottom: '1px solid #e6e3ea' }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 700, flex: 1 }}>
                  {model.agencyName}
                </Typography>
                <Box textAlign="right" sx={{ fontSize: 10, color: '#6b6675' }}>
                  <Box>
                    {t('travelServices.circuits.print.quotedOn', {
                      date: model.generatedAt,
                    })}
                  </Box>
                  {model.advisor && (
                    <Box>
                      {t('travelServices.circuits.print.yourAdvisor', {
                        name: model.advisor.name,
                      })}
                      {model.advisor.contact.length > 0
                        ? ` · ${model.advisor.contact.join(' · ')}`
                        : ''}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Carátula. */}
              <Box className="print-block" mt={2}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
                  {model.title}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#6b6675', mt: 0.5 }}>
                  {[
                    model.operatedBy &&
                      t('travelServices.circuits.print.operatedBy', {
                        provider: model.operatedBy,
                      }),
                    model.durationDays &&
                      t('travelServices.durationDays', { count: model.durationDays }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                {model.chain.stops.length > 0 && (
                  <Box mt={0.75}>
                    <CircuitItineraryChain
                      stops={model.chain.stops}
                      withDays={model.chain.withDays}
                      tail={model.chain.truncated ? '…' : undefined}
                    />
                  </Box>
                )}
                {model.flags.length > 0 && (
                  <Box display="flex" gap={0.75} mt={1} flexWrap="wrap">
                    {model.flags.map((flag) => (
                      <FlagChip key={flag} flag={flag} />
                    ))}
                  </Box>
                )}
                {model.mapUrl && (
                  <Box
                    component="img"
                    src={model.mapUrl}
                    alt=""
                    sx={{
                      display: 'block',
                      width: 'auto',
                      maxWidth: '100%',
                      maxHeight: 260,
                      objectFit: 'contain',
                      mt: 1.5,
                      mx: 'auto',
                      borderRadius: 1,
                    }}
                  />
                )}
              </Box>

              <Box
                className="print-block"
                display="grid"
                gridTemplateColumns={{ xs: '1fr', sm: 'minmax(0, 1fr) 210px' }}
                gap={2}
                mt={1}
              >
                {model.includes.length > 0 ? (
                  <Box>
                    <SectionTitle>
                      {t('travelServices.circuits.includes')}
                    </SectionTitle>
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                      {model.includes.map((item, index) => (
                        <Typography
                          key={index}
                          component="li"
                          sx={{
                            fontSize: 10.5,
                            lineHeight: 1.45,
                            color: '#4c4757',
                            py: 0.25,
                            pl: 2,
                            position: 'relative',
                            whiteSpace: 'pre-line',
                            '&::before': {
                              content: '"✓"',
                              position: 'absolute',
                              left: 0,
                              color: '#1e5c22',
                              fontWeight: 700,
                              fontSize: 10,
                            },
                          }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box />
                )}
                {model.priceFrom && (
                  <Box
                    sx={{
                      border: '1px solid #e6e3ea',
                      borderRadius: 1,
                      p: 1.25,
                      alignSelf: 'start',
                      mt: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: 9.5, color: '#6b6675' }}>
                      {t('travelServices.priceFrom')}
                    </Typography>
                    <Typography sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.1 }}>
                      {model.priceFrom}
                    </Typography>
                    <Typography sx={{ fontSize: 9.5, color: '#6b6675' }}>
                      {t('travelServices.circuits.print.perPersonDoubleLong')}
                    </Typography>
                    {/* El precio impreso declara su edad: sin esto, un PDF
                        reenviado tres semanas después se lee como vigente. */}
                    <Typography sx={{ fontSize: 8.5, color: '#8d8798', mt: 0.75 }}>
                      {t('travelServices.circuits.print.indicativeOn', {
                        date: model.generatedAt,
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>

              {model.days.length > 0 && (
                <Box>
                  <SectionTitle>
                    {t('travelServices.circuits.itineraryByDay')}
                  </SectionTitle>
                  {model.days.map((day) => (
                    <Box
                      key={day.day}
                      className="print-block"
                      display="grid"
                      gridTemplateColumns="34px minmax(0, 1fr)"
                      gap={1.25}
                      py={0.75}
                    >
                      <Typography
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          color: '#4a148c',
                          bgcolor: '#f1ecf7',
                          borderRadius: 0.75,
                          textAlign: 'center',
                          py: 0.25,
                          height: 'fit-content',
                        }}
                      >
                        {`D${String(day.day).padStart(2, '0')}`}
                      </Typography>
                      <Box minWidth={0}>
                        {day.title && (
                          <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>
                            {day.title}
                          </Typography>
                        )}
                        {day.text && (
                          <Typography
                            sx={{
                              fontSize: 10,
                              lineHeight: 1.5,
                              color: '#4c4757',
                              whiteSpace: 'pre-line',
                            }}
                          >
                            {day.text}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {model.optionals.length > 0 && (
                <Box>
                  <SectionTitle>
                    {t('travelServices.circuits.optionalsAside')}
                  </SectionTitle>
                  {/* Por qué no hay importes acá: ver `CircuitPrintOptional`. */}
                  <Typography sx={{ fontSize: 9, color: '#8d8798', mb: 0.5 }}>
                    {t('travelServices.circuits.print.optionalPriceNote')}
                  </Typography>
                  {model.optionals.map((optional) => (
                    <Box
                      key={optional.id}
                      className="print-block"
                      display="grid"
                      gridTemplateColumns="minmax(0, 1fr)"
                      gap={1.25}
                      py={0.75}
                      pl={optional.selected ? 1 : 0}
                      sx={
                        optional.selected
                          ? {
                            bgcolor: '#f6f3fa',
                            borderLeft: '2.5px solid #4a148c',
                          }
                          : undefined
                      }
                    >
                      <Box minWidth={0}>
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: optional.selected ? 600 : 500,
                            color: optional.selected ? '#221f26' : '#6b6675',
                          }}
                        >
                          {optional.name}
                          {optional.selected && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: 8.5,
                                fontWeight: 700,
                                color: '#4a148c',
                                bgcolor: '#ece3f6',
                                borderRadius: 0.75,
                                px: 0.75,
                                py: 0.125,
                                ml: 0.875,
                              }}
                            >
                              {t('travelServices.circuits.print.selectedBadge')}
                            </Box>
                          )}
                        </Typography>
                        <Typography sx={{ fontSize: 9.5, color: '#6b6675' }}>
                          {[
                            ...optional.meta,
                            !optional.selected &&
                              t('travelServices.circuits.print.alsoAvailable'),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Box>
                      <Box />
                    </Box>
                  ))}
                </Box>
              )}

              {/* Pie: se repite en cada página impresa. */}
              <Box
                className="print-foot"
                display="flex"
                justifyContent="space-between"
                gap={2}
                mt={3}
                pt={1}
                sx={{ borderTop: '1px solid #e6e3ea', fontSize: 8.5, color: '#8d8798' }}
              >
                <Box component="span">
                  {t('travelServices.circuits.print.generatedOn', {
                    date: model.generatedAt,
                  })}
                </Box>
                <Box component="span">{model.agencyName}</Box>
              </Box>
            </Box>
          </AudienceProvider>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<Close />}>
          {t('travelServices.booking.close')}
        </Button>
        <Button
          variant="contained"
          startIcon={<Print />}
          // El PDF lo hace el navegador: "Guardar como PDF" en el diálogo de
          // impresión. Cero dependencias para generar papel.
          onClick={() => window.print()}
        >
          {t('travelServices.circuits.print.action')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
